import type { Page } from '../components/PageConfigProvider/types';
import type { SecondaryDimensionValues } from '../components/PageConfigProvider/types/dimension';
import {
  type DimensionSource,
  DimensionType,
  type PositionedItem,
} from '../components/PageConfigProvider/types/item';
import { FilterId, type PrimaryFilterId } from '../filters/types/filters';

// Backend format for dimensionSource
type BackendDimensionSource = {
  type?: string;
  segment?: SecondaryDimensionValues;
};

// Transform a Page object: deserialize dimensionSource from backend format to frontend format
export const deserializePageDimensionSources = (page: Page): Page => {
  const transformItem = (item: PositionedItem) => {
    if (!item.plot) return item;

    const plot = { ...item.plot };

    // Deserialize dimensionSource
    if (plot.dimensionSource && typeof plot.dimensionSource === 'object') {
      plot.dimensionSource = deserializeDimensionSource(
        plot.dimensionSource as unknown as BackendDimensionSource
      );
    }

    // Deserialize alternateDimensionSource array
    if (
      plot.alternateDimensionSource &&
      Array.isArray(plot.alternateDimensionSource)
    ) {
      plot.alternateDimensionSource = plot.alternateDimensionSource
        .filter((source) => typeof source === 'object')
        .map((source: BackendDimensionSource) =>
          deserializeDimensionSource(source)
        )
        .filter((source): source is DimensionSource => source !== undefined);
    }

    return { ...item, plot };
  };

  return {
    ...page,
    tabs: page.tabs.map((tab) => ({
      ...tab,
      tabContent: {
        ...tab.tabContent,
        items: tab.tabContent?.items?.map(transformItem) ?? [],
      },
    })),
  };
};

export const deserializeDimensionSource = (
  backend: BackendDimensionSource | undefined
): DimensionSource | undefined => {
  if (!backend) return undefined;
  if (backend.type === DimensionType.PRIMARY_DIMENSION) {
    return 'primary';
  }
  if (backend.type === DimensionType.TOP_SEGMENTS && backend.segment) {
    return backend.segment;
  }
  return undefined;
};

export const dimensionSourceToFilterId = (
  dimensionSource: SecondaryDimensionValues | string
): PrimaryFilterId | undefined => {
  const upperDimensionSource = dimensionSource.toUpperCase();
  if (upperDimensionSource === 'COMPANY') return FilterId.COMPANY;
  if (upperDimensionSource === 'GEOGRAPHY') return FilterId.GEOGRAPHY;
  if (upperDimensionSource === 'ROLE') return FilterId.ROLE;
  return undefined;
};
